import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
// form
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
// @mui
import {
  Stack,
  OutlinedInput,
  FormHelperText,
  Alert,
  Link,
  Box,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
// hooks
import useAuth from 'hooks/useAuth'
import useLocales from 'hooks/useLocales'
import useGuestOtpChallenge from 'hooks/domains/Users/useGuestOtpChallenge'
// components
import { FormProvider } from 'components/hook-form'
import { useAuthStore } from '../../store'
import { getSessionStorage } from 'helpers/session-storage'
import { getUrlQueryParam } from 'helpers/routing'
import { renderErrorMessage } from 'helpers/auth/error-auth'
import { getErrorStatus } from 'helpers/error'
import { Typography } from '@mui/material'
import { sendLog } from 'helpers/log'
import {
  isCaptchaCancellation,
  isCaptchaFailure,
} from 'helpers/captcha/contracts'
import trackEvent from '~/trackers'
import { useRouter } from 'next/router'
import { setPeopleProperties } from 'helpers/analytics'
import { handleMaxLengthInput } from 'helpers/formats/number'
import { VerificationAction } from '../../store/Auth/interface'

// ----------------------------------------------------------------------

type FormValuesProps = {
  code1: string
  code2: string
  code3: string
  code4: string
  code5: string
  code6: string
  afterSubmit?: string
}

type ValueNames =
  | 'code1'
  | 'code2'
  | 'code3'
  | 'code4'
  | 'code5'
  | 'code6'

export default function VerifyCodeForm({
  inputPhoneNumber = '',
  otpAction = 'SIGN_IN',
  redirectHomeOnSuccess = false,
  onSuccess,
  onVerify,
  onRequestOTP,
  onInviteTokenInvalid,
}: {
  inputPhoneNumber?: string
  otpAction?: VerificationAction
  // Navigate to '/' after a successful verify — '/' is the gateway that
  // routes an authenticated user to the right dashboard. For the standalone
  // sign-in page, which has nowhere else to send them; dialogs that stay put
  // (OTPDialog) leave this off and use onSuccess instead.
  redirectHomeOnSuccess?: boolean
  onSuccess?: Function
  onVerify?: Function
  onRequestOTP?: Function
  // Called when the invite token in the URL is expired, so there is no code to
  // enter. The caller owns the heading and description above this form, so it
  // has to replace the whole page — this component cannot do it alone.
  onInviteTokenInvalid?: () => void
}) {
  const { push, pathname, query } = useRouter()
  const trackerData = { pathname, query }
  const { t } = useLocales()
  const { verify, loginPhone, loginPhoneByInvitation, user } = useAuth()
  const { runGuestOtp, invalidate } = useGuestOtpChallenge()

  const { enqueueSnackbar } = useSnackbar()

  // Present when the user arrives directly from a team-invite email/WhatsApp
  // link (…/auth/verify/?invite_token=...) rather than through the normal
  // in-app login flow. Read straight off the browser URL — next/router's
  // query object can still be empty on first render.
  const invitationToken = getUrlQueryParam('invite_token')

  const notifySuccess = () => {
    onSuccess && onSuccess()
    if (redirectHomeOnSuccess) push('/')
  }

  const [seconds, setSeconds] = useState(60)
  const [, setTimer] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    verificationMethod,
    checkVerificationMethod,
    clearAuthStore,
    setVerificationMethod,
    countryPhone,
  } = useAuthStore((state) => ({
    verificationMethod: state.verificationMethod,
    checkVerificationMethod: state.checkVerificationMethod,
    clearAuthStore: state.clearAuthStore,
    setVerificationMethod: state.setVerificationMethod,
    countryPhone: state.countryPhone,
  }))

  const authUsername =
    inputPhoneNumber || getSessionStorage('inputPhoneNumber')

  // verificationMethod is only ever set by the in-app login page
  // (setVerificationMethod) before navigating here — a direct invite-link
  // visit skips that step entirely, so it stays '' for the whole flow.
  // Fall back to 'WA' everywhere it picks a delivery channel.
  const deliveryMethod = verificationMethod || 'WA'

  const VerifyCodeSchema = Yup.object().shape({
    code1: Yup.string().required('Code is required'),
    code2: Yup.string().required('Code is required'),
    code3: Yup.string().required('Code is required'),
    code4: Yup.string().required('Code is required'),
    code5: Yup.string().required('Code is required'),
    code6: Yup.string().required('Code is required'),
  })

  const defaultValues = {
    code1: '',
    code2: '',
    code3: '',
    code4: '',
    code5: '',
    code6: '',
  }

  const methods = useForm<FormValuesProps>({
    mode: 'all',
    resolver: yupResolver(VerifyCodeSchema),
    defaultValues,
  })

  const {
    watch,
    control,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = methods

  const values = watch()

  const stopTimer = () => {
    setTimer((interval) => {
      clearInterval(interval)
      return null
    })
  }

  const startTimer = () => {
    setTimer(
      setInterval(() => {
        setSeconds((seconds) => seconds - 1)
      }, 1000),
    )
  }

  useEffect(() => {
    if (seconds === 0) {
      stopTimer()
      clearErrors([
        'code1',
        'code2',
        'code3',
        'code4',
        'code5',
        'code6',
      ])
    }
  }, [seconds])

  useEffect(() => {
    trackEvent.auth('visit_hub_phone_number_verification', {
      ...trackerData,
      verificationMethod,
    })
    startTimer()
    // Prevent verification method disapear when user refresh page
    checkVerificationMethod(verificationMethod)

    const target = document.querySelector('input.field-code')

    target?.addEventListener('paste', handlePaste)

    return () => {
      target?.removeEventListener('paste', handlePaste)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Team-invite accept flow: there's no prior in-app "send OTP" step (the
  // user landed here straight from the invite email/WhatsApp link), so fire
  // it once on mount using the invite_token instead of a typed phone
  // number. The resolved phone comes back on `user.phoneNumber`, same as the
  // normal flow. Skipped when the caller owns OTP sending (onRequestOTP),
  // so an unrelated dialog on a URL that happens to carry invite_token
  // never fires an invitation send of its own.
  const invitationSendFiredRef = useRef(false)
  useEffect(() => {
    if (onRequestOTP || !invitationToken || invitationSendFiredRef.current)
      return
    invitationSendFiredRef.current = true
    // Keep the store in step with what we actually send, so the "sent via X"
    // copy and the switch-method link don't contradict the real channel.
    if (!verificationMethod) setVerificationMethod(deliveryMethod)
    invalidate()
    loginPhoneByInvitation(
      invitationToken,
      deliveryMethod,
      runGuestOtp,
    ).catch((error) => {
      if (isCaptchaCancellation(error) || isCaptchaFailure(error)) {
        // No OTP went out, so the countdown would only keep resend and the
        // method switch locked. Release them right away.
        stopTimer()
        setSeconds(0)
        if (isCaptchaFailure(error)) {
          setError('afterSubmit', {
            type: 'custom',
            message: t('key_captcha_verification_failed'),
          })
        }
        return
      }
      sendLog(error)
      // An expired invite token answers NOT_FOUND / 404. There is no code to
      // enter in that case, so the OTP form is the wrong thing to show at all
      // — hand it to the caller, which replaces the page. Without this the
      // form rendered the raw axios message ("Request failed with status code
      // 404") under a heading still asking for a code.
      if (
        error?.code === 'NOT_FOUND' ||
        getErrorStatus(error) === 404
      ) {
        onInviteTokenInvalid?.()
        return
      }
      const errorMessage = renderErrorMessage(error?.code, {
        seconds: error.data?.retry_in_secs,
        defaultMessage: error.message,
      })
      setError('afterSubmit', { ...error, message: errorMessage })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationToken])

  const handlePaste = (event: any) => {
    let data = event.clipboardData.getData('text')

    data = data.split('')
    ;[].forEach.call(
      document.querySelectorAll('.field-code'),
      (node: any, index) => {
        node.value = data[index]

        const fieldIndex = `code${index + 1}`

        setValue(fieldIndex as ValueNames, data[index])
      },
    )

    event.preventDefault()
  }

  const handleDelete = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key == 'Delete' || event.key == 'Backspace') {
      const inputs = event.currentTarget

      const fieldIndex = inputs.name.replace('code', '')

      const fieldIntIndex = Number(fieldIndex)

      if (fieldIntIndex > 1 && inputs.value == '') {
        const nextfield = document.querySelector(
          `input[name=code${fieldIntIndex - 1}]`,
        )

        if (nextfield !== null) {
          ;(nextfield as HTMLElement).focus()
        }
      }
    }
  }

  const handleChangeWithNextField = (
    event: React.ChangeEvent<HTMLInputElement>,
    handleChange: (
      // eslint-disable-next-line no-unused-vars
      event: React.ChangeEvent<HTMLInputElement>,
    ) => void,
  ) => {
    const { maxLength, value, name } = event.target

    const fieldIndex = name.replace('code', '')

    const fieldIntIndex = Number(fieldIndex)

    if (value.length >= maxLength) {
      if (fieldIntIndex < 6) {
        const nextfield = document.querySelector(
          `input[name=code${fieldIntIndex + 1}]`,
        )

        if (nextfield !== null) {
          ;(nextfield as HTMLElement).focus()
        }
      }
    }

    clearErrors()
    handleChange(event)
  }

  const handleChangeVerificationMethod = () => {
    const selectedVerificationMethod =
      verificationMethod === 'WA' ? 'SMS' : 'WA'
    setVerificationMethod(selectedVerificationMethod)
  }

  const onSubmit = async (data: FormValuesProps) => {
    try {
      setIsLoading(true)
      const code = Object.values(data).join('')
      if (onVerify) {
        await onVerify(code)
      } else {
        // Invitation flow: the phone was resolved by BE (from the token) and
        // returned on user.phoneNumber, already fully formatted — don't
        // re-prefix it with the dial code like the typed-number flow does.
        const username = invitationToken
          ? user?.phoneNumber || ''
          : `${countryPhone.dial_code}${authUsername}`
        await verify(
          code,
          deliveryMethod,
          username,
          otpAction,
          invitationToken || undefined,
        )
        trackEvent.auth(
          'trigger_verified_phone',
          { ...trackerData, verificationMethod },
          {
            phone_number_verification_success: true,
          },
        )
        setPeopleProperties({
          phone_number_verification_status: 'verified',
        })

        enqueueSnackbar(t('key_otp_verify_success'))
        clearAuthStore()
      }
      notifySuccess()
    } catch (error) {
      trackEvent.auth(
        'trigger_verified_phone',
        { ...trackerData, verificationMethod },
        {
          phone_number_verification_success: false,
        },
      )
      setPeopleProperties({
        phone_number_verification_status: 'unverified',
      })
      const errorMessage = renderErrorMessage(
        error?.errors?.[0]?.err || error?.code,
        {
          seconds: error.data?.retry_in_secs,
          defaultMessage: error.message,
        },
      )
      // The error is surfaced to the user via setError above. Don't re-reject:
      // nothing consumes handleSubmit()'s promise, so a rejection here only
      // becomes an unhandled one on every failed attempt.
      setError('afterSubmit', {
        ...error,
        message: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const authCodeResend = async (isSwitchMethod: boolean = false) => {
    try {
      const method = isSwitchMethod
        ? verificationMethod === 'WA'
          ? 'SMS'
          : 'WA'
        : deliveryMethod
      if (onRequestOTP) {
        await setVerificationMethod(method)
        await onRequestOTP(method)
      } else if (invitationToken) {
        await loginPhoneByInvitation(
          invitationToken,
          method,
          runGuestOtp,
        )
      } else {
        await loginPhone(authUsername, method, runGuestOtp, otpAction)
      }
      if (isSwitchMethod) {
        trackEvent.auth(
          `click_verification_method_${method.toLowerCase()}`,
          { ...trackerData, verificationMethod: method },
        )
        handleChangeVerificationMethod()
      } else {
        trackEvent.auth('trigger_resend_phone_verification', {
          ...trackerData,
          verificationMethod: method,
        })
      }
    } catch (error) {
      if (!isCaptchaCancellation(error) && !isCaptchaFailure(error)) {
        sendLog(error)
      }
      return Promise.reject(error)
    }
  }

  const resendCode = async (isSwitchMethod: boolean = false) => {
    invalidate()
    try {
      setIsLoading(true)
      await authCodeResend(isSwitchMethod)
      clearErrors([
        'code1',
        'code2',
        'code3',
        'code4',
        'code5',
        'code6',
      ])
      setIsLoading(false)
      setSeconds(60)
      startTimer()
    } catch (error) {
      setIsLoading(false)
      if (isCaptchaCancellation(error)) return
      if (isCaptchaFailure(error)) {
        setError('afterSubmit', {
          type: 'custom',
          message: t('key_captcha_verification_failed'),
        })
        return
      }
      //handle general error
      sendLog(error.message)

      if (
        error?.code?.toUpperCase() ===
          'USER_REQUEST_OTP_REACH_MAXIMUM' ||
        error?.code?.toUpperCase() === 'TOO_MANY_REQUESTS'
      ) {
        setSeconds(error.data?.retry_in_secs || 60)
        startTimer()
      }

      const errorMessage = renderErrorMessage(error?.code, {
        seconds: error.data?.retry_in_secs,
        defaultMessage: error.message,
      })
      setError('afterSubmit', {
        ...error,
        message: errorMessage,
      })
    }
  }

  const handleResendCode = () => {
    if (seconds > 0 || isLoading) return () => {}
    return resendCode(true)
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} justifyContent="center">
          {Object.keys(values).map((name, index) => (
            <Controller
              key={name}
              name={`code${index + 1}` as ValueNames}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <OutlinedInput
                  id={`i-users-verifycodeform-${name}`}
                  {...field}
                  error={!!error}
                  autoFocus={index === 0}
                  placeholder="-"
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ) =>
                    handleChangeWithNextField(event, field.onChange)
                  }
                  onInput={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ) => {
                    handleMaxLengthInput(event, 1)
                  }}
                  onKeyDown={handleDelete}
                  inputProps={{
                    className: 'field-code',
                    maxLength: 1,
                    sx: {
                      p: 0,
                      textAlign: 'center',
                      width: { xs: 36, sm: 56 },
                      height: { xs: 36, sm: 56 },
                    },
                  }}
                  type="number"
                />
              )}
            />
          ))}
        </Stack>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        {(!!errors.code1 ||
          !!errors.code2 ||
          !!errors.code3 ||
          !!errors.code4 ||
          !!errors.code5 ||
          !!errors.code6) && (
          <FormHelperText error sx={{ px: 2 }}>
            {t('key_code_is_required')}
          </FormHelperText>
        )}

        <LoadingButton
          id="b-users-verifycodeform-submit"
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting || isLoading}
          sx={{ mt: 3 }}
          disabled={isLoading}
        >
          {t('key_verify')}
        </LoadingButton>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="body2">
              {t('key_havent_received_the_code')} &nbsp;
            </Typography>
            <Link
              id="b-users-verifycodeform-resend-code"
              sx={(theme) => ({
                color:
                  (seconds > 0 || isLoading) &&
                  theme.palette.text.disabled,
                cursor: !(seconds > 0 || isLoading) && 'pointer',
                textDecoration: 'none !important',
              })}
              variant="subtitle2"
              onClick={
                seconds > 0 || isLoading
                  ? () => {}
                  : () => resendCode()
              }
            >
              {t('key_resend')}{' '}
            </Link>
            {seconds > 0 && (
              <Typography>
                (
                {seconds > 60
                  ? `${Math.ceil(seconds / 60)} ${t('key_minutes')}`
                  : `${seconds} ${t('key_seconds')}`}
                )
              </Typography>
            )}
          </Box>

          {countryPhone?.allow_sms && (
            <Typography
              variant="body2"
              sx={{ color: '#A1A0A6', marginTop: '12px !important' }}
            >
              {t('key_or_send_via')} &nbsp;
              <Link
                id="b-users-verifycodeform-send-via-alt"
                variant="subtitle2"
                onClick={handleResendCode}
                sx={(theme) => ({
                  color:
                    (seconds > 0 || isLoading) &&
                    theme.palette.text.disabled,
                  cursor: !(seconds > 0 || isLoading) && 'pointer',
                  textDecoration: 'none !important',
                })}
              >
                {verificationMethod === 'WA' ? 'SMS' : 'WhatsApp'}
              </Link>
            </Typography>
          )}
        </Box>
      </Stack>
    </FormProvider>
  )
}
